import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import type { ChangeStats } from '../types/app';

const allowedTags = [
  'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 's', 'del',
  'a', 'blockquote', 'pre', 'code', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'hr',
];

const purifierConfig: Config = {
  ALLOWED_TAGS: allowedTags,
  ALLOWED_ATTR: ['href', 'title', 'rowspan', 'colspan', 'target', 'rel'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'option', 'svg', 'math', 'img', 'video', 'audio', 'link', 'meta'],
  FORBID_ATTR: ['style', 'class', 'id', 'src', 'srcset'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[#/]|\.\.?\/)/i,
  KEEP_CONTENT: true,
};

function normalizeSemanticTags(html: string): string {
  const documentNode = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  documentNode.body.querySelectorAll('b').forEach((node) => {
    const replacement = documentNode.createElement('strong');
    replacement.replaceChildren(...node.childNodes);
    node.replaceWith(replacement);
  });
  documentNode.body.querySelectorAll('i').forEach((node) => {
    const replacement = documentNode.createElement('em');
    replacement.replaceChildren(...node.childNodes);
    node.replaceWith(replacement);
  });
  documentNode.body.querySelectorAll('del').forEach((node) => {
    const replacement = documentNode.createElement('s');
    replacement.replaceChildren(...node.childNodes);
    node.replaceWith(replacement);
  });
  documentNode.body.querySelectorAll('a[href]').forEach((node) => {
    const href = node.getAttribute('href') ?? '';
    if (/^https?:\/\//i.test(href)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return documentNode.body.innerHTML;
}

export function sanitizeHtml(input: string, stats?: ChangeStats, simplify = true): string {
  const conditionalComments = input.match(/<!--\s*\[if[\s\S]*?<!\s*\[endif\]\s*-->/gi)?.length ?? 0;
  const cleaned = input.replace(/<!--\s*\[if[\s\S]*?<!\s*\[endif\]\s*-->/gi, '');
  const config: Config = simplify ? purifierConfig : { ...purifierConfig, ALLOWED_TAGS: [...allowedTags, 'div', 'span'] };
  const sanitized = DOMPurify.sanitize(cleaned, config);
  if (stats) {
    stats.unsafeHtmlRemoved += conditionalComments + DOMPurify.removed.length;
  }
  return normalizeSemanticTags(sanitized);
}

export function setSanitizedHtml(element: HTMLElement, html: string): void {
  element.innerHTML = sanitizeHtml(html, undefined, false);
}
